import { useState, useEffect, useMemo } from 'react';
import {
  CartLineT,
  CartT,
  LineItemType,
  UpdateLineItemDataT,
  UpdateLineItemsDataT,
} from '@lambda/apis/src/cart/types';
import { useCookies } from 'react-cookie';
import { RC_DURATION } from '@lambda/reebelo';
import { uniq, uniqBy } from 'lodash-es';
import settings from '@/settings';
import { AddToCartParams, CartProviderResults, ToBeRemovedT } from './types';
import {
  createCart,
  createCartLineItem,
  createCartLineItems,
  deleteCartLineItem,
  deleteCartLineItems,
  fetchCartById,
  updateCartLineItem,
  updateCartLineItems,
} from './helpers';
import { notNull } from '@/components/category/helpers';
import {
  trackReebeloCareRemoved,
  trackRemoveFromCart,
} from '@/lib/analytics/trackProduct';
import { updateParentItemDeliveryNotes } from '@/lib/order';
import { CART_COOKIE_NAME } from './constants';

const useCart = (): CartProviderResults => {
  const [cookies, setCookie] = useCookies([CART_COOKIE_NAME]);
  const [cart, setCart] = useState<CartT | null>(null);
  const [cartLoading, setCartLoading] = useState<boolean>(false);
  const [cartInitialized, setCartInitialized] = useState<boolean>(false);
  const [reviewLines, setReviewLines] = useState<ToBeRemovedT[]>([]);

  const createAndSetNewCart = async () => {
    const newCart = await createCart({ store: settings.store });

    if (newCart) {
      setCart(newCart);
      setCookie(CART_COOKIE_NAME, newCart.id, { path: '/', maxAge: 1209600 }); // Cookie to expire in 14 days
    }
  };

  // Initialize Cart
  const initializeCart = async (initialCart?: CartT | null): Promise<void> => {
    try {
      if (initialCart) {
        setCart(initialCart);

        return;
      }

      const cartId = cookies[CART_COOKIE_NAME];

      if (cartId) {
        const existingCart = await fetchCartById(cartId);

        if (existingCart) {
          setCart(existingCart);
        } else {
          await createAndSetNewCart();
        }
      } else {
        await createAndSetNewCart();
      }
    } finally {
      setCartInitialized(true);
    }
  };

  useEffect(() => {
    if (cart) {
      setCartInitialized(true);
    }
  }, [cart]);

  useEffect(() => {
    if (!cartInitialized) {
      initializeCart();
    }
  }, []);

  // Clear Cart
  const clearCart = async () => {
    await createAndSetNewCart();
  };

  // Line Item In Cart
  const getLineItemInCart = (typeId: string) =>
    cart?.lines?.find((line) => line.typeId === typeId) ?? null;

  // Add To Cart
  const addToCart = async ({
    quantity = 1,
    notes,
    typeId,
    type,
    reference,
  }: AddToCartParams) => {
    if (cart && typeId.length > 0) {
      setCartLoading(true);
      const lineItemInCart = getLineItemInCart(typeId);

      try {
        let updatedCart;

        if (lineItemInCart && lineItemInCart.type === LineItemType.PRODUCT) {
          updatedCart = await updateCartLineItem(cart.id, lineItemInCart.id, {
            quantity: lineItemInCart.quantity + quantity,
            notes,
          });
        } else {
          updatedCart = await createCartLineItem(cart.id, {
            type,
            typeId,
            quantity,
            notes,
            reference,
          });
        }

        if (updatedCart) {
          setCart(updatedCart);

          return updatedCart; // Return the updated cart
        }
      } finally {
        setCartLoading(false);
      }
    }

    return null; // Return null if cart is not updated
  };

  // Update Cart
  const updateCart = async (
    lineItemId: string,
    lineItem: UpdateLineItemDataT,
  ) => {
    if (cart && lineItemId.length > 0) {
      setCartLoading(true);

      try {
        const updatedCart = await updateCartLineItem(
          cart.id,
          lineItemId,
          lineItem,
        );

        if (updatedCart) {
          setCart(updatedCart);

          return updatedCart; // Return the updated cart
        }
      } finally {
        setCartLoading(false);
      }
    }

    return null; // Return null if the cart wasn't updated
  };

  // Tracks a line leaving the cart. A ReebeloCare line under a device is
  // reported as ReebeloCare removed; anything else as a product removed.
  const trackLineRemoved = (
    lineItem: CartLineT,
    parentItem: CartLineT | undefined,
    skipReebeloCareTracking?: boolean,
    skipProductRemovedTracking?: boolean,
  ) => {
    const isReebeloCareItem = lineItem.type === LineItemType.WARRANTY;

    if (isReebeloCareItem && parentItem) {
      // The caller may have already tracked this removal with its own
      // entry_point (e.g. the cart toggle / mobile pop-up CTA).
      if (skipReebeloCareTracking) {
        return;
      }

      const reebeloCareDuration = (lineItem?.notes?.find(
        (note) => note.name === 'Duration',
      )?.value ?? RC_DURATION['24_MONTH']) as RC_DURATION;

      trackReebeloCareRemoved({
        entry_point: 'Cart',
        reebelocare_id: lineItem.id,
        device_psku: parentItem.psku ?? '',
        device_vendor: parentItem?.vendor ?? '',
        device_sku: parentItem?.sku ?? '',
        duration: reebeloCareDuration,
      });

      return;
    }

    if (skipProductRemovedTracking) {
      return;
    }

    trackRemoveFromCart({
      cartLine: lineItem,
    });
  };

  // Remove From Cart
  const removeFromCart = async (
    lineItem: CartLineT,
    parentItem: CartLineT | undefined,
    parentUpdatedNotes?: Map<string, string>,
    skipReebeloCareTracking?: boolean,
    skipProductRemovedTracking?: boolean,
  ) => {
    if (cart && lineItem) {
      setCartLoading(true);

      try {
        const isExpressShippingItem = lineItem.type === LineItemType.SHIPPING;

        if (isExpressShippingItem && parentItem && parentUpdatedNotes) {
          await updateParentItemDeliveryNotes({
            parentItem,
            update: updateCart,
            notes: parentUpdatedNotes,
          });
        }

        trackLineRemoved(
          lineItem,
          parentItem,
          skipReebeloCareTracking,
          skipProductRemovedTracking,
        );

        const _cart = await deleteCartLineItem(cart.id, lineItem.id);

        if (_cart) {
          setCart(_cart);
        }
      } finally {
        setCartLoading(false);
      }
    }
  };

  // Remove several lines from the cart in one request. MsCart also drops the
  // warranty and express-shipping lines attached to each of them.
  const removeLinesFromCart = async (
    lineItems: CartLineT[],
    options?: { skipReebeloCareTracking?: boolean },
  ) => {
    if (!cart || lineItems.length === 0) {
      return null;
    }

    const uniqueLineItems = uniqBy(lineItems, 'id');

    setCartLoading(true);

    try {
      uniqueLineItems.forEach((lineItem) => {
        const parentItem = cart.lines.find(
          (line) => line.id === lineItem.reference?.typeId,
        );

        trackLineRemoved(
          lineItem,
          parentItem,
          options?.skipReebeloCareTracking,
        );
      });

      const updatedCart = await deleteCartLineItems(
        cart.id,
        uniqueLineItems.map((lineItem) => lineItem.id),
      );

      if (updatedCart) {
        setCart(updatedCart);

        return updatedCart;
      }
    } finally {
      setCartLoading(false);
    }

    return null;
  };

  // Add several lines in one request. As with `addToCart`, a product already
  // in the cart has its quantity raised instead: those go in one update
  // request ahead of the add. An add-on line (warranty, express shipping)
  // must reference a line already in the cart.
  const addLinesToCart = async (lineItems: AddToCartParams[]) => {
    const lines = lineItems.filter(({ typeId }) => typeId.length > 0);

    if (!cart || lines.length === 0) {
      return null;
    }

    const inCart = (line: AddToCartParams) => {
      const existing = getLineItemInCart(line.typeId);

      return existing?.type === LineItemType.PRODUCT ? existing : null;
    };

    const increases = lines.flatMap((line) => {
      const existing = inCart(line);

      return existing
        ? [
            {
              id: existing.id,
              quantity: existing.quantity + (line.quantity ?? 1),
              notes: line.notes,
            },
          ]
        : [];
    });
    const additions = lines
      .filter((line) => !inCart(line))
      .map(({ quantity = 1, notes, typeId, type, reference }) => ({
        type,
        typeId,
        quantity,
        notes,
        reference,
      }));

    setCartLoading(true);

    try {
      let updatedCart: CartT | null = cart;

      // Each request's cart is kept as soon as it succeeds: if the add then
      // fails, the increases are already in the cart on the server. Still
      // null overall, so callers don't report lines that weren't added.
      if (increases.length > 0) {
        updatedCart = await updateCartLineItems(cart.id, increases);

        if (updatedCart) {
          setCart(updatedCart);
        }
      }

      if (updatedCart && additions.length > 0) {
        updatedCart = await createCartLineItems(cart.id, additions);

        if (updatedCart) {
          setCart(updatedCart);
        }
      }

      return updatedCart;
    } finally {
      setCartLoading(false);
    }
  };

  // Update several lines in one request. MsCart keeps the warranty and
  // express-shipping lines attached to each of them at the same quantity.
  const updateCartLines = async (lines: UpdateLineItemsDataT['lines']) => {
    if (!cart || lines.length === 0) {
      return null;
    }

    setCartLoading(true);

    try {
      const updatedCart = await updateCartLineItems(
        cart.id,
        uniqBy(lines, 'id'),
      );

      if (updatedCart) {
        setCart(updatedCart);

        return updatedCart;
      }
    } finally {
      setCartLoading(false);
    }

    return null;
  };

  // Cart Count
  const cartCount = useMemo(
    () =>
      cart?.lines
        ?.filter((line) => line.type === LineItemType.PRODUCT)
        ?.reduce((count, item) => count + item.quantity, 0) || 0,
    [cart],
  );

  // Cart Lines
  const cartLines = useMemo(() => cart?.lines || [], [cart]);

  const cartLinesCategories = uniq(
    cartLines.map((cartLine) => cartLine.category?.name).filter(notNull),
  );

  const cartLinesIds = uniq(
    cartLines.map((cartLine) => cartLine.id).filter(notNull),
  );

  const getWarrantyForProduct = (productTypeId: string) => {
    const warranty = cartLines.find(
      (line) =>
        line.type === LineItemType.WARRANTY &&
        line.reference?.typeId === productTypeId,
    );

    return warranty;
  };

  const getExpressShippingForProduct = (productTypeId: string) =>
    cartLines.find(
      (line) =>
        line.type === LineItemType.SHIPPING &&
        line.reference?.typeId === productTypeId,
    );

  return {
    // State
    cart,
    cartLoading,
    cartCount,
    cartInitialized,
    cartLines,
    cartLinesCategories,
    cartLinesIds,

    // API methods
    addToCart,
    updateCart,
    removeFromCart,
    removeLinesFromCart,
    addLinesToCart,
    updateCartLines,
    getLineItemInCart,
    clearCart,
    setCart,
    initializeCart,
    getWarrantyForProduct,
    getExpressShippingForProduct,
    reviewLines,
    setReviewLines,
  };
};

export default useCart;
